import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Trash2 } from "lucide-react";
import {
    getPosterPresignUrl,
    createPoster,
    fetchPosters,
    deletePoster,
    type Poster,
} from "../services/posterService";
import { useModal } from '../../../shared/components/ModalProvider';

type ApiErrorPayload = {
    message?: string;
};

const getErrorMessage = (error: unknown, fallback: string) => {
    if (axios.isAxiosError(error)) {
        const payload = error.response?.data as ApiErrorPayload | undefined;
        return payload?.message || error.message || fallback;
    }
    if (error instanceof Error) {
        return error.message || fallback;
    }
    return fallback;
};

const SuperaminPosterPage: React.FC = () => {
    const modal = useModal();
    const [posters, setPosters] = useState<Poster[]>([]);
    const [posterFile, setPosterFile] = useState<File | null>(null);
    const [posterPreview, setPosterPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingPosters, setLoadingPosters] = useState(false);
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
    const posterFileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        loadPosters();
    }, []);

    const loadPosters = async () => {
        setLoadingPosters(true);
        try {
            const data = await fetchPosters();
            setPosters(data);
        } catch (error) {
            const message = getErrorMessage(error, "포스터 목록 조회 실패");
            await modal.alert(message);
        } finally {
            setLoadingPosters(false);
        }
    };

    const handlePosterFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                await modal.alert("이미지 파일만 업로드 가능합니다.");
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                await modal.alert("파일 크기는 10MB 이하여야 합니다.");
                return;
            }
            setPosterFile(file);
            const reader = new FileReader();
            reader.onload = (event) => {
                setPosterPreview(event.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDeletePoster = async (posterId: number) => {
        if (!await modal.confirm("이 포스터를 삭제하시겠습니까?")) {
            return;
        }

        try {
            await deletePoster(posterId);
            await modal.alert("포스터가 삭제되었습니다.");
            loadPosters();
        } catch (error) {
            const message = getErrorMessage(error, "포스터 삭제 실패");
            await modal.alert(message);
        }
    };

    const handlePosterUpload = async () => {
        if (!posterFile) {
            await modal.alert("파일을 선택해주세요.");
            return;
        }

        setLoading(true);
        try {
            const presignData = await getPosterPresignUrl(
                posterFile.name,
                posterFile.type,
            );

            const uploadResponse = await fetch(presignData.uploadUrl, {
                method: "PUT",
                headers: {
                    "Content-Type": posterFile.type,
                },
                body: posterFile,
            });

            if (!uploadResponse.ok) {
                throw new Error("이미지 업로드 실패");
            }

            await createPoster(presignData.imageUrl);

            await modal.alert("포스터가 업로드되었습니다.");
            setPosterFile(null);
            setPosterPreview(null);
            if (posterFileInputRef.current) {
                posterFileInputRef.current.value = "";
            }
            loadPosters();
        } catch (error) {
            const message = getErrorMessage(error, "포스터 업로드 실패");
            await modal.alert(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='p-8'>
            {/* 헤더 */}
            <div className='mb-6'>
                <h1 className='text-32-semibold text-gray-900 mb-2'>
                    포스터 관리
                </h1>
                <p className='text-16-regular text-gray-600'>
                    TV 화면에 표시될 포스터를 관리합니다.
                </p>
            </div>

            {/* 포스터 업로드 */}
            <div className='bg-white rounded-lg shadow p-6 mb-6'>
                <h2 className='text-20-semibold text-gray-900 mb-4'>
                    포스터 업로드
                </h2>
                <div className='max-w-md'>
                    <input
                        ref={posterFileInputRef}
                        type='file'
                        accept='image/*'
                        onChange={handlePosterFileSelect}
                        className='hidden'
                    />
                    <div className='flex gap-2'>
                        <button
                            onClick={() => posterFileInputRef.current?.click()}
                            className='px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition'
                            disabled={loading}
                        >
                            파일 선택
                        </button>
                        <button
                            onClick={handlePosterUpload}
                            disabled={loading || !posterFile}
                            className='px-4 py-2 bg-main text-white rounded-lg font-medium hover:bg-main-medium transition disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            {loading ? "업로드 중..." : "업로드"}
                        </button>
                    </div>
                    {posterFile && (
                        <p className='mt-2 text-14-regular text-gray-600'>
                            선택된 파일: {posterFile.name}
                        </p>
                    )}
                    {posterPreview && (
                        <div className='mt-4'>
                            <img
                                src={posterPreview}
                                alt='미리보기'
                                className='max-w-full h-auto max-h-48 rounded-lg border border-gray-200'
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* 포스터 목록 */}
            <div className='bg-white rounded-lg shadow p-6'>
                <h2 className='text-20-semibold text-gray-900 mb-4'>
                    포스터 목록
                </h2>
                {loadingPosters ? (
                    <div className='text-center py-8 text-gray-500'>
                        포스터 목록을 불러오는 중...
                    </div>
                ) : posters.length === 0 ? (
                    <div className='text-center py-8 text-gray-500'>
                        등록된 포스터가 없습니다.
                    </div>
                ) : (
                    <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
                        {posters.map((poster) => (
                            <div
                                key={poster.posterId}
                                className='border border-gray-200 rounded-lg overflow-hidden'
                            >
                                <img
                                    src={poster.imageUrl}
                                    alt={`포스터 ${poster.posterId}`}
                                    className='w-full h-auto object-cover cursor-pointer hover:opacity-80 transition-opacity'
                                    onClick={() => setLightboxUrl(poster.imageUrl)}
                                />
                                <div className='p-2 flex items-center justify-between'>
                                    <span className='text-12-regular text-gray-500'>
                                        {new Date(
                                            poster.createdAt,
                                        ).toLocaleDateString("ko-KR")}
                                    </span>
                                    <button
                                        onClick={() =>
                                            handleDeletePoster(poster.posterId)
                                        }
                                        className='p-1 text-gray-400 hover:text-red-500 transition-colors'
                                        title='삭제'
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 라이트박스 */}
            {lightboxUrl && (
                <div
                    className='fixed inset-0 z-50 bg-black/80 flex items-center justify-center cursor-pointer'
                    onClick={() => setLightboxUrl(null)}
                >
                    <img
                        src={lightboxUrl}
                        alt='포스터 확대'
                        className='max-w-[90vw] max-h-[90vh] object-contain rounded-lg'
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
};

export default SuperaminPosterPage;